import React from "react";
import { useParams } from "react-router-dom";
import { useActivateConnection } from "@features/ix/erpIntegration/hooks";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { Check } from "lucide-react";

interface ActivateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isActivating: boolean;
  syncFlowsCount: number;
}

export const ActivateDialog: React.FC<ActivateDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isActivating: parentIsActivating,
  syncFlowsCount,
}) => {
  const { id } = useParams<{ id: string }>();
  const activateMutation = useActivateConnection(id || "");

  const isActivating = parentIsActivating || activateMutation.isPending;

  const handleConfirm = () => {
    activateMutation.mutate(undefined, {
      onSuccess: () => {
        onConfirm();
      },
    });
  };
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[540px] p-0 overflow-hidden gap-0">
        {/* Header */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-gray-100 flex items-center justify-between">
          <DialogTitle className="text-lg font-bold text-gray-900 leading-none">
            Activate this integration?
          </DialogTitle>
        </DialogHeader>

        {/* Body */}
        <div className="px-6 py-6 space-y-4">
          <p className="text-sm text-gray-600 leading-relaxed font-normal">
            Activating will start syncing data immediately. You can pause it at
            any time from the Integration Detail view.
          </p>

          <div className="bg-emerald-50/50 border border-emerald-100 rounded-xl p-4 flex gap-3 items-start">
            <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 shrink-0 mt-0.5">
              <Check className="w-3 h-3" strokeWidth={3} />
            </div>
            <div className="space-y-0.5">
              <h4 className="text-sm font-bold text-emerald-800 leading-normal">
                Pre-flight checks passed
              </h4>
              <p className="text-xs text-emerald-650 font-semibold leading-relaxed">
                Connection verified · {syncFlowsCount} sync flows configured
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-white">
          <Button variant="outline" onClick={onClose} disabled={isActivating}>
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleConfirm}
            disabled={isActivating}
          >
            {isActivating ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Activating...</span>
              </>
            ) : (
              "Yes, Activate"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ActivateDialog;
